import type { ConsentRowDto, SendRowDto, SuppressionRowDto } from '@carbonwebmail/contracts'
import type { PillTone } from '../../ui/StatusPill.js'

/**
 * Wording and colour for the states these screens show.
 *
 * Here rather than in each screen because a suppression reason means the same
 * thing wherever it appears, and two spellings of "hard bounce" is how a table
 * and a filter stop agreeing.
 *
 * Each map is keyed by the contract's own union rather than by `string`. That
 * makes every lookup total — no fallback branch, no "possibly undefined" — and
 * it means adding a case to the contract fails the build here until someone
 * decides what it should say. A `Record<string, …>` would have quietly rendered
 * nothing for it.
 */

interface Display {
  readonly label: string
  readonly tone: PillTone
}

export const REASONS: Record<SuppressionRowDto['reason'], Display> = {
  unsubscribed: { label: 'unsubscribed', tone: 'neutral' },
  hard_bounce: { label: 'hard bounce', tone: 'danger' },
  complaint: { label: 'complaint', tone: 'danger' },
  manual: { label: 'manual', tone: 'neutral' },
}

/**
 * `unknown` has its own tone and never the danger one. The provider never
 * answered, so the mail may well have been delivered — painting it red asserts
 * something the system does not know. (I7)
 */
export const SEND_STATUS: Record<SendRowDto['status'], Display> = {
  accepted: { label: 'accepted', tone: 'ok' },
  duplicate: { label: 'duplicate', tone: 'ok' },
  rejected: { label: 'rejected', tone: 'danger' },
  failed: { label: 'failed', tone: 'warn' },
  unknown: { label: 'unknown', tone: 'unknown' },
}

/** What each consent event means, in the words a regulator would expect. */
export const CONSENT: Record<ConsentRowDto['type'], Display> = {
  // Not 'ok'. Someone asserted consent by uploading a file; nobody confirmed
  // anything, and a green tick here would read as proof that does not exist.
  imported: { label: 'imported', tone: 'neutral' },
  signup_requested: { label: 'signup requested', tone: 'neutral' },
  confirmation_sent: { label: 'confirmation sent', tone: 'neutral' },
  /** The only moment consent actually comes into being. */
  confirmed: { label: 'confirmed', tone: 'ok' },
  unsubscribe_requested: { label: 'unsubscribe requested', tone: 'neutral' },
  unsubscribed: { label: 'unsubscribed', tone: 'neutral' },
  /**
   * The one event here that is not the subscriber's own action. Warn rather
   * than neutral: somebody inside the organisation decided a suppression no
   * longer applies, and a reader scanning this trail should see it.
   */
  suppression_removed: { label: 'suppression removed', tone: 'warn' },
}
